import React, { useEffect, useState } from 'react';
import { trackCTAClick } from 'utils/analytics';
import { localePath } from 'utils/localePath';
import GoogleLoginButton from 'components/shared/GoogleLoginButton';

const VALID_FORCED_ROLES = ['teacher', 'student'];

const LoginModal = ({ isOpen, onClose, translations, onLoginSuccess, teamName = null, initialState = 'login', forcedRole: forcedRoleProp = null }) => {
  // An invalid forcedRole would skip role selection AND fail server-side role
  // validation — a dead end with no role selector to recover from. Treat any
  // non-whitelisted value as "no lock" so the normal two-step flow applies.
  const forcedRole = VALID_FORCED_ROLES.includes(forcedRoleProp) ? forcedRoleProp : null;
  if (forcedRoleProp && !forcedRole && process.env.NODE_ENV !== 'production') {
    console.warn(`[LoginModal] Ignoring invalid forcedRole "${forcedRoleProp}" — expected 'teacher' or 'student'`);
  }
  const [csrfToken, setCsrfToken] = useState('');
  const [modalState, setModalState] = useState(initialState); // login | register | forgot_password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  // Two-step register: 'role_select' (pick teacher/student) → 'form' (Google + email form).
  // forcedRole skips role selection entirely — the modal never offers another role.
  const [registerStep, setRegisterStep] = useState(forcedRole ? 'form' : 'role_select');
  const effectiveRole = forcedRole || role;
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const t = translations.login_modal || {};

  useEffect(() => {
    const token = document.querySelector('[name="csrf-token"]')?.content;
    if (token) setCsrfToken(token);
  }, []);

  // Reset form when modal opens or state changes
  useEffect(() => {
    if (isOpen) {
      setErrors([]);
      setIsLoading(false);
      setResetSent(false);
    }
  }, [isOpen, modalState]);

  // Reset to initialState when modal closes — register always reopens on step 1
  // (or directly on the form when forcedRole is set); step 2 must never render
  // with an empty role.
  useEffect(() => {
    if (!isOpen) {
      setModalState(initialState);
      setEmail('');
      setPassword('');
      setName('');
      setRole('');
      setRegisterStep(forcedRole ? 'form' : 'role_select');
      setErrors([]);
      setResetSent(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
          active.blur(); // First ESC: dismiss autocomplete / unfocus input
          return;
        }
        onClose(); // Second ESC: close modal
      }
    };

    if (!isOpen) return;

    // Restore whatever was there, not a literal. Writing 'auto' back leaves an
    // inline style on body that overrides the stylesheet and makes body a
    // scroll container, which stops every page-level position: sticky.
    const previousOverflow = document.body.style.overflow;
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const jsonFetch = async (url, body) => {
    const token = document.querySelector('[name="csrf-token"]')?.content || csrfToken;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    });
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return { success: false, errors: [t.login_error || 'Something went wrong. Please try again.'] };
    }
    return response.json();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors([]);
    setIsLoading(true);
    try {
      const data = await jsonFetch('/users/sign_in.json', { user: { email, password } });
      if (data.success) {
        if (onLoginSuccess) {
          window.location.reload();
        } else {
          window.location.href = data.redirect_to;
        }
      } else if (data.unconfirmed) {
        // Show resend confirmation option
        setModalState('unconfirmed');
      } else {
        setErrors(data.errors || []);
      }
    } catch {
      setErrors([t.login_error || 'Something went wrong']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrors([]);

    if (!effectiveRole) {
      setErrors([t.role_required || 'Please select your role']);
      return;
    }
    if (password.length < 6) {
      setErrors([t.password_too_short || 'Password must be at least 6 characters']);
      return;
    }

    setIsLoading(true);
    try {
      const data = await jsonFetch('/users.json', { user: { name, email, password, role: effectiveRole } });
      if (data.success) {
        if (data.confirmation_required) {
          // Email confirmation needed — show confirmation sent message
          setModalState('confirmation_sent');
        } else if (onLoginSuccess) {
          window.location.reload();
        } else {
          window.location.href = data.redirect_to;
        }
      } else {
        setErrors(data.errors || []);
      }
    } catch {
      setErrors([t.login_error || 'Something went wrong']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setErrors([]);
    setIsLoading(true);
    try {
      const data = await jsonFetch('/users/confirmation.json', { user: { email } });
      if (data.success) {
        setResetSent(true); // reuse resetSent to show success message
      } else {
        setErrors(data.errors || []);
      }
    } catch {
      setErrors([t.login_error || 'Something went wrong']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErrors([]);
    setIsLoading(true);
    try {
      const data = await jsonFetch('/users/password.json', { user: { email } });
      if (data.success) {
        setResetSent(true);
      } else {
        setErrors(data.errors || []);
      }
    } catch {
      setErrors([t.login_error || 'Something went wrong']);
    } finally {
      setIsLoading(false);
    }
  };

  const switchState = (newState) => {
    setModalState(newState);
    // Navigating between states resets the register sub-step and the chosen role
    setRegisterStep(forcedRole ? 'form' : 'role_select');
    if (!forcedRole) setRole('');
    setErrors([]);
    setResetSent(false);
  };

  const selectRole = (chosenRole) => {
    trackCTAClick('signup_role_selected', `login_modal_role_${chosenRole}`);
    setRole(chosenRole);
    setErrors([]);
    setRegisterStep('form');
  };

  const backToRoleSelect = () => {
    setRole('');
    setErrors([]);
    setRegisterStep('role_select');
  };

  // Single Google OAuth button.
  // - login state (no forcedRole): role-neutral — no role param; new emails get
  //   bounced to the registration page by the OAuth callback
  // - register step 2 / forcedRole: carries the chosen or forced role.
  //   Note: with forcedRole the LOGIN state also carries the role by design —
  //   on a role-locked surface (student invitation) a new email completing
  //   OAuth from "Log in" should still join as that role, not get bounced.
  const GoogleButton = ({ oauthRole, surface }) => (
    <GoogleLoginButton
      role={oauthRole || undefined}
      label={t.google_continue || 'Continue with Google'}
      onSubmit={() => {
        if (oauthRole) {
          trackCTAClick(`${oauthRole}_signup`, surface, `/users/auth/google_oauth2?role=${oauthRole}`);
        } else {
          trackCTAClick('google_login_neutral', surface, '/users/auth/google_oauth2');
        }
      }}
      translations={t}
    />
  );

  const TermsConsent = () => (
    <p className="text-xs text-gray-400 text-center">
      {t.terms_consent || 'By signing up, you agree to our'}{' '}
      <a href={localePath("/terms")} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 underline">
        {t.terms_link || 'Terms of Service'}
      </a>
      {' '}{t.terms_consent_and || 'and'}{' '}
      <a href={localePath("/privacy")} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 underline">
        {t.privacy_link || 'Privacy Policy'}
      </a>
    </p>
  );

  const Divider = () => (
    <div className="flex items-center my-4">
      <div className="flex-1 border-t border-gray-200"></div>
      <span className="px-3 text-sm text-gray-400">{t.or_divider || 'or'}</span>
      <div className="flex-1 border-t border-gray-200"></div>
    </div>
  );

  const ErrorMessages = () => errors.length > 0 ? (
    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
      {errors.map((err, i) => (
        <p key={i} className="text-sm text-red-700">{err}</p>
      ))}
    </div>
  ) : null;

  const getTitle = () => {
    if (teamName) {
      const joinTitle = t.join_team_title || 'Join %{team_name}';
      return joinTitle.replace('%{team_name}', teamName);
    }
    if (modalState === 'register') return t.register_title || 'Create Account';
    if (modalState === 'forgot_password') return t.reset_password_title || 'Reset Password';
    if (modalState === 'confirmation_sent') return t.confirmation_sent || 'Check your email';
    if (modalState === 'unconfirmed') return t.unconfirmed_title || 'Email not verified';
    return t.login_title || 'Log In';
  };

  const getSubtitle = () => {
    if (modalState === 'register') {
      return registerStep === 'form'
        ? (t.role_step_subtitle_form || 'Choose how to sign up')
        : (t.role_step_subtitle_select || 'First, pick your role');
    }
    if (modalState === 'forgot_password') return t.reset_password_subtitle || "Enter your email and we'll send you a reset link";
    if (modalState === 'confirmation_sent') return '';
    if (modalState === 'unconfirmed') return '';
    return t.login_subtitle || 'Sign in to your account';
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile header: back arrow — on register step 2 it goes back to role
            selection (one step back); everywhere else it closes the modal */}
        <div className="lg:hidden px-4 py-3 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={
              modalState === 'register' && registerStep === 'form' && !forcedRole
                ? backToRoleSelect
                : onClose
            }
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label={
              modalState === 'register' && registerStep === 'form' && !forcedRole
                ? (t.change_role || 'Change')
                : (t.close || 'Close')
            }
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <span className="font-semibold text-gray-900 block">{getTitle()}</span>
            {getSubtitle() && <span className="text-xs text-gray-500">{getSubtitle()}</span>}
          </div>
        </div>

        {/* Desktop header: blue bar */}
        <div className="hidden lg:flex px-6 py-4 border-b flex-shrink-0 items-center justify-between" style={{ backgroundColor: '#E6F0FF' }}>
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#3366FF' }}>
              {getTitle()}
            </h2>
            <p className="text-sm mt-1" style={{ color: '#5588FF' }}>
              {getSubtitle()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            aria-label={t.close || 'Close'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* ===== LOGIN STATE ===== */}
          {modalState === 'login' && (
            <div className="space-y-4">
              <GoogleButton oauthRole={forcedRole} surface="login_modal_login" />
              <TermsConsent />

              <Divider />

              <form onSubmit={handleLogin} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.email_label || 'Email'}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.email_placeholder || 'your@email.com'}
                    required
                    autoComplete="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.password_label || 'Password'}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.password_placeholder || 'Enter password'}
                    required
                    autoComplete="current-password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                </div>

                <ErrorMessages />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#3366FF' }}
                >
                  {isLoading ? '...' : (t.login_button || 'Log In')}
                </button>
              </form>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => switchState('forgot_password')}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {t.forgot_password || 'Forgot password?'}
                </button>
              </div>

              <div className="text-center text-sm text-gray-600">
                {t.no_account || "Don't have an account?"}{' '}
                <button
                  type="button"
                  onClick={() => switchState('register')}
                  className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  {t.signup_link || 'Sign up'}
                </button>
              </div>
            </div>
          )}

          {/* ===== REGISTER STATE — STEP 1: role selection ===== */}
          {modalState === 'register' && registerStep === 'role_select' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => selectRole('teacher')}
                  className="p-5 rounded-xl border-2 border-gray-200 bg-white text-gray-700 text-center transition-all duration-200 hover:border-[#3366FF] hover:bg-[#E6F0FF] focus-visible:outline-none focus-visible:border-[#3366FF] focus-visible:bg-[#E6F0FF]"
                >
                  <div className="text-3xl mb-2">👨‍🏫</div>
                  <div className="font-semibold">{t.role_teacher || 'Teacher'}</div>
                  <div className="text-xs mt-1 opacity-70">{t.teacher_role_desc || 'Manage classes & students'}</div>
                </button>
                <button
                  type="button"
                  onClick={() => selectRole('student')}
                  className="p-5 rounded-xl border-2 border-gray-200 bg-white text-gray-700 text-center transition-all duration-200 hover:border-[#3366FF] hover:bg-[#E6F0FF] focus-visible:outline-none focus-visible:border-[#3366FF] focus-visible:bg-[#E6F0FF]"
                >
                  <div className="text-3xl mb-2">👨‍🎓</div>
                  <div className="font-semibold">{t.role_student || 'Student'}</div>
                  <div className="text-xs mt-1 opacity-70">{t.student_role_desc || 'Find & book tutors'}</div>
                </button>
              </div>

              <div className="text-center text-sm text-gray-600">
                {t.has_account || 'Already have an account?'}{' '}
                <button
                  type="button"
                  onClick={() => switchState('login')}
                  className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  {t.login_link || 'Log in'}
                </button>
              </div>
            </div>
          )}

          {/* ===== REGISTER STATE — STEP 2: auth methods for the chosen role ===== */}
          {modalState === 'register' && registerStep === 'form' && (
            <div className="space-y-4">
              {!forcedRole && effectiveRole && (
                <div className="flex items-center justify-between px-4 py-2.5 rounded-lg" style={{ backgroundColor: '#E6F0FF' }}>
                  <span className="text-sm font-medium" style={{ color: '#3366FF' }}>
                    {(t.selected_role_label || 'Signing up as: %{role}').replace(
                      '%{role}',
                      effectiveRole === 'teacher' ? (t.role_teacher || 'Teacher') : (t.role_student || 'Student')
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={backToRoleSelect}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    {t.change_role || 'Change'}
                  </button>
                </div>
              )}

              <GoogleButton oauthRole={effectiveRole} surface="login_modal_register_step2" />
              <Divider />

              <form onSubmit={handleRegister} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.name_label || 'Name'}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.name_placeholder || 'Your name'}
                    required
                    autoComplete="name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.email_label || 'Email'}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.email_placeholder || 'your@email.com'}
                    required
                    autoComplete="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.password_label || 'Password'}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.password_placeholder || 'Enter password'}
                    required
                    autoComplete="new-password"
                    minLength={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                  <p className="mt-1 text-xs text-gray-400">{t.password_min_length || 'At least 6 characters'}</p>
                </div>

                <ErrorMessages />

                <button
                  type="submit"
                  disabled={isLoading || !effectiveRole}
                  className="w-full py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#3366FF' }}
                >
                  {isLoading ? '...' : (t.signup_button || 'Sign Up')}
                </button>

                <TermsConsent />
              </form>

              <div className="text-center text-sm text-gray-600">
                {t.has_account || 'Already have an account?'}{' '}
                <button
                  type="button"
                  onClick={() => switchState('login')}
                  className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  {t.login_link || 'Log in'}
                </button>
              </div>
            </div>
          )}

          {/* ===== FORGOT PASSWORD STATE (email auth only) ===== */}
          {modalState === 'forgot_password' && (
            <div className="space-y-4">
              {resetSent ? (
                <div className="text-center py-4">
                  <div className="text-4xl mb-3">✉️</div>
                  <p className="text-gray-700">
                    {t.reset_email_sent || "If this email is registered, you'll receive a password reset link"}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t.email_label || 'Email'}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.email_placeholder || 'your@email.com'}
                      required
                      autoFocus
                      autoComplete="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    />
                  </div>

                  <ErrorMessages />

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#3366FF' }}
                  >
                    {isLoading ? '...' : (t.send_reset_link || 'Send Reset Link')}
                  </button>
                </form>
              )}

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => switchState('login')}
                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  {t.back_to_login || 'Back to login'}
                </button>
              </div>
            </div>
          )}

          {/* ===== UNCONFIRMED STATE ===== */}
          {modalState === 'unconfirmed' && (
            <div className="text-center py-6 space-y-4">
              {resetSent ? (
                <>
                  <div className="text-5xl">✉️</div>
                  <p className="text-gray-700 font-medium">
                    {t.confirmation_resent || 'Verification email resent!'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {t.confirmation_sent_detail || 'Click the link in the email to activate your account'}
                  </p>
                </>
              ) : (
                <>
                  <div className="text-5xl">⚠️</div>
                  <p className="text-gray-700 font-medium">
                    {t.unconfirmed_error || 'Your email has not been verified yet'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {t.unconfirmed_hint || 'Check your inbox for the verification email, or resend it below'}
                  </p>

                  <ErrorMessages />

                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={isLoading}
                    className="w-full py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#3366FF' }}
                  >
                    {isLoading ? '...' : (t.resend_confirmation || 'Resend verification email')}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => switchState('login')}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
              >
                {t.back_to_login || 'Back to login'}
              </button>
            </div>
          )}

          {/* ===== CONFIRMATION SENT STATE ===== */}
          {modalState === 'confirmation_sent' && (
            <div className="text-center py-6 space-y-4">
              <div className="text-5xl">✉️</div>
              <p className="text-gray-700 font-medium">
                {t.confirmation_sent || 'Verification email sent — please check your inbox'}
              </p>
              <p className="text-sm text-gray-500">
                {t.confirmation_sent_detail || 'Click the link in the email to activate your account'}
              </p>
              <button
                type="button"
                onClick={() => switchState('login')}
                className="mt-4 px-6 py-2 rounded-lg text-white font-medium transition-colors"
                style={{ backgroundColor: '#3366FF' }}
              >
                {t.back_to_login || 'Back to login'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
